/**
 * DFC artwork that changes with the light / dark mode and is still to come from DFC (court
 * booking deck, 12/10). Each slot is empty until the file arrives — import it and set it here.
 * Every consumer falls back to what it shows today, so an empty slot changes nothing.
 *
 * Village lockups live with the village metadata (`fitnessVillages.ts`), not here.
 */

export type DfcArtworkMode = 'light' | 'dark';

export type DfcModeArtwork = Partial<Record<DfcArtworkMode, string>>;

/**
 * The home hero visual, in place of the "What's your 30x30?" line (Basel, 20/10). A missing
 * mode keeps the words; it never borrows the other mode's artwork.
 *
 * "30 minutes | 30 days": a transparent PNG with black text on green and blue pills, so one
 * file reads on both the white and the black page. Referenced from Firebase Storage, not
 * bundled, so DFC's visual can be swapped by replacing the file in storage.
 */
const DFC_HERO_VISUAL_URL =
  'https://firebasestorage.googleapis.com/v0/b/skmd-299311.appspot.com/o/Dubai%20Fitness%20Challenge%2FZone%20Images%2F30%20Minutes%2030%20days-01.png?alt=media&token=9868f2e9-59ee-413e-90b9-b8267b382143';

export const DFC_HERO_ARTWORK: DfcModeArtwork = {
  light: DFC_HERO_VISUAL_URL,
  dark: DFC_HERO_VISUAL_URL,
};

/**
 * The header logo drawn for dark backgrounds. Light mode always uses the admin panel's
 * `company.logo`; dark mode uses this when set, without the white plate behind it.
 */
export const DFC_HEADER_LOGO_DARK: string | undefined = undefined;

/**
 * DFC's `company.logo` is a transparent SVG in blue and green that reads on black (checked
 * 12/10), so dark mode shows it on the page without the white plate. Set false if the admin
 * panel's logo is ever replaced with one that needs the plate.
 */
export const DFC_HEADER_LOGO_WORKS_ON_DARK = true;

/** The artwork for the page's mode, if DFC has supplied it. */
export const getModeArtwork = (artwork: DfcModeArtwork, isDark: boolean): string | undefined =>
  artwork[isDark ? 'dark' : 'light'];
